import { useTranslation } from 'react-i18next'

import { ActivationBadge } from '../../components/ActivationPanel'
import { ComplianceBadge } from '../brokers/ComplianceBadge'
import {
  describeReason,
  unavailableBecause,
  type CarrierStanding,
  type FleetStanding,
} from './fleetStanding'

/**
 * The standing of one fleet row, told in full.
 *
 * Showing only the record's own paperwork is how a screen ends up reading "Compliant"
 * beside somebody nobody can send anywhere — the exact shape of silence this project
 * has already paid for twice.
 */
export function FleetStandingCell({
  record,
  carrier,
}: {
  record: FleetStanding
  carrier: CarrierStanding
}) {
  const { t } = useTranslation()

  // The compliance badge already tells the record's own paperwork story, and the
  // inactive chip tells its own stand-down. What is left is what nothing on the row
  // would otherwise reveal — above all, a carrier that took its whole fleet with it.
  const unsaid = unavailableBecause(record, carrier).filter(
    (reason) => reason.key !== 'compliance.standDownOwn' && reason.key !== 'compliance.ownPaperwork',
  )

  return (
    <span className="standing">
      <ComplianceBadge state={record.complianceState} />
      <ActivationBadge active={record.active} />
      {unsaid.map((reason) => (
        <span key={reason.key} className="muted">
          {describeReason(reason, t)}
        </span>
      ))}
    </span>
  )
}

/**
 * The same truth as a sentence, for the screens that show one record at a time and
 * have no row of chips to lean on.
 */
export function FleetAvailabilityNote({
  record,
  carrier,
}: {
  record: FleetStanding
  carrier: CarrierStanding
}) {
  const { t } = useTranslation()
  const reasons = unavailableBecause(record, carrier)

  if (reasons.length === 0) {
    return null
  }

  return (
    <p className="page__subtitle">
      <strong>{t('compliance.unavailable')}</strong>{' '}
      {reasons
        .filter((reason) => reason.key !== 'compliance.unavailable')
        .map((reason) => describeReason(reason, t))
        .join(' · ')}
    </p>
  )
}
